import React, { useState, useEffect } from "react";
import { useBackendHealth } from "../context/BackendHealthContext";

const RETRY_SECS = 8;

/* ─────────────────────────────────────────────────────────────────────────
   SVG-Illustration
   Blickwinkel: vom Netz aus auf den Sandplatz.
   Der Server (Rack) steht an der Grundlinie und setzt zum Aufschlag an.
   ───────────────────────────────────────────────────────────────────────── */
const CourtScene = () => (
  <svg
    viewBox="0 0 320 168"
    width="320"
    height="168"
    style={{ display: "block", margin: "0 auto" }}
    aria-label="Server setzt zum Aufschlag an der Grundlinie an"
  >
    <defs>
      {/* Himmel – hell und sonnig */}
      <linearGradient id="skyG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="#2e8fd4" />
        <stop offset="100%" stopColor="#87cfee" />
      </linearGradient>
      {/* Platz: oben dunkler (Perspektive / Schatten) */}
      <linearGradient id="courtG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="#a03c18" />
        <stop offset="100%" stopColor="#d0622e" />
      </linearGradient>
      {/* Nahplatz (vor dem Netz, Empfängerseite) */}
      <linearGradient id="nearG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="#c85a28" />
        <stop offset="100%" stopColor="#b04820" />
      </linearGradient>
      {/* Tennisball */}
      <radialGradient id="ballG" cx="38%" cy="32%">
        <stop offset="0%"   stopColor="#eeff55" />
        <stop offset="100%" stopColor="#9ab814" />
      </radialGradient>
      {/* Schatten-Filter für Server-Rack */}
      <filter id="rackShadow" x="-40%" y="-40%" width="180%" height="180%">
        <feDropShadow dx="1" dy="3" stdDeviation="2.5" floodColor="rgba(0,0,0,0.45)" />
      </filter>
    </defs>

    {/* ── Himmel ─────────────────────────────────────────────────────── */}
    <rect x="0" y="0" width="320" height="76" fill="url(#skyG)" />

    {/* ── Sonne ──────────────────────────────────────────────────────── */}
    <g style={{ animation: "tgt-sun-spin 18s linear infinite", transformBox: "fill-box", transformOrigin: "center" }}>
      {/* Strahlen */}
      {[0,30,60,90,120,150,180,210,240,270,300,330].map(deg => {
        const rad = deg * Math.PI / 180;
        return (
          <line key={deg}
            x1={272 + Math.cos(rad) * 15}
            y1={22  + Math.sin(rad) * 15}
            x2={272 + Math.cos(rad) * 24}
            y2={22  + Math.sin(rad) * 24}
            stroke="#ffe066" strokeWidth="2.5" strokeLinecap="round" opacity="0.9"
          />
        );
      })}
    </g>
    {/* Sonnenscheibe */}
    <circle cx="272" cy="22" r="11" fill="#ffd700" />
    <circle cx="272" cy="22" r="11" fill="#ffe94d" opacity="0.6" />

    {/* ── Grüner Untergrund (hinter Grundlinie + Seiten) ─────────────── */}
    <rect x="0" y="54" width="320" height="22" fill="#2e8b2e" />
    {/* Grüne Streifen links und rechts neben dem Platz */}
    <polygon points="0,76 94,76 20,162 0,162"     fill="#2e8b2e" />
    <polygon points="226,76 320,76 320,162 300,162" fill="#2e8b2e" />

    {/* ── Bäume – hintere Reihe (dunkler) ────────────────────────────── */}
    <ellipse cx="0"   cy="46" rx="26" ry="20" fill="#1e6e1e" />
    <ellipse cx="48"  cy="42" rx="34" ry="25" fill="#207020" />
    <ellipse cx="105" cy="44" rx="30" ry="22" fill="#1e6e1e" />
    <ellipse cx="160" cy="41" rx="28" ry="21" fill="#207020" />
    <ellipse cx="215" cy="44" rx="30" ry="22" fill="#1e6e1e" />
    <ellipse cx="272" cy="42" rx="34" ry="25" fill="#207020" />
    <ellipse cx="320" cy="46" rx="26" ry="20" fill="#1e6e1e" />

    {/* ── Bäume – vordere Reihe (heller, größer) ──────────────────────── */}
    <ellipse cx="0"   cy="57" rx="24" ry="18" fill="#38b838" />
    <ellipse cx="36"  cy="54" rx="30" ry="22" fill="#3aba3a" />
    <ellipse cx="78"  cy="58" rx="26" ry="19" fill="#32b032" />
    <ellipse cx="114" cy="61" rx="22" ry="16" fill="#38b838" />
    <ellipse cx="206" cy="61" rx="22" ry="16" fill="#38b838" />
    <ellipse cx="242" cy="58" rx="26" ry="19" fill="#32b032" />
    <ellipse cx="284" cy="54" rx="30" ry="22" fill="#3aba3a" />
    <ellipse cx="320" cy="57" rx="24" ry="18" fill="#38b838" />

    {/* ── Büsche / Hecke seitlich neben dem Platz ─────────────────────── */}
    <ellipse cx="8"   cy="100" rx="14" ry="28" fill="#2ea02e" />
    <ellipse cx="6"   cy="138" rx="12" ry="22" fill="#32a832" />
    <ellipse cx="312" cy="100" rx="14" ry="28" fill="#2ea02e" />
    <ellipse cx="314" cy="138" rx="12" ry="22" fill="#32a832" />

    {/* Zaun hinter Grundlinie */}
    <line x1="92" y1="70" x2="228" y2="70" stroke="#bbb" strokeWidth="1.5" opacity="0.6" />
    {[100,112,124,136,148,160,172,184,196,208,220].map(x => (
      <line key={x} x1={x} y1="65" x2={x} y2="74" stroke="#ccc" strokeWidth="1" opacity="0.45" />
    ))}

    {/* ── Spielfeld (Aufschläger-Seite, Trapez) ──────────────────────── */}
    {/*  Grundlinie (hinten):  x = 94–226,  y = 74
        Netzlinie  (vorne):   x = 20–300,  y = 162             */}
    <polygon points="20,162 300,162 226,74 94,74" fill="url(#courtG)" />

    {/* ── Feldlinien ─────────────────────────────────────────────────── */}
    {/* Grundlinie */}
    <line x1="94"  y1="74"  x2="226" y2="74"  stroke="white" strokeWidth="2.5" />
    {/* Mittelmarke */}
    <line x1="160" y1="74"  x2="160" y2="79"  stroke="white" strokeWidth="2.5" />
    {/* Seitenlinien */}
    <line x1="20"  y1="162" x2="94"  y2="74"  stroke="white" strokeWidth="2" />
    <line x1="300" y1="162" x2="226" y2="74"  stroke="white" strokeWidth="2" />

    {/* Aufschlaglinie  – y=114  (≈ 44 % der Platzhöhe von hinten)
        Linkes x  = 20  + (94–20)  * (162–114)/(162–74) = 20  + 74*48/88 = 60.4 → 61
        Rechtes x = 300 – (300–226)* 48/88                = 300 – 40.4      = 259.6 → 260  */}
    <line x1="61"  y1="114" x2="259" y2="114" stroke="white" strokeWidth="1.5" />
    {/* Mittellinie (Aufschlagfelder: Netz → Aufschlaglinie) */}
    <line x1="160" y1="162" x2="160" y2="114" stroke="white" strokeWidth="1.5" />

    {/* ── Netzschatten auf Platz ─────────────────────────────────────── */}
    <rect x="20" y="162" width="280" height="6" fill="rgba(0,0,0,0.18)" />

    {/* ── Netz ───────────────────────────────────────────────────────── */}
    {/* Pfosten */}
    <rect x="12"  y="144" width="9" height="24" rx="2" fill="#cccccc" />
    <rect x="299" y="144" width="9" height="24" rx="2" fill="#cccccc" />
    {/* Netzband oben */}
    <rect x="12" y="144" width="296" height="5" rx="1" fill="white" opacity="0.95" />
    {/* Maschen-Vertikalen */}
    {[30,48,66,84,102,120,138,156,174,192,210,228,246,264,282].map(x => (
      <line key={x} x1={x} y1="149" x2={x} y2="163" stroke="rgba(255,255,255,0.55)" strokeWidth="1" />
    ))}
    {/* Maschen-Horizontalen */}
    <line x1="12" y1="153" x2="308" y2="153" stroke="rgba(255,255,255,0.35)" strokeWidth="0.9" />
    <line x1="12" y1="158" x2="308" y2="158" stroke="rgba(255,255,255,0.28)" strokeWidth="0.9" />

    {/* ── SERVER an der Grundlinie ────────────────────────────────────── */}
    {/*
        Koordinaten-System: translate(170, 74) = Fußpunkt des Servers
        an der Grundlinie, leicht rechts der Mitte (Einstandseite).
        Innere rotate(-12, 0, -16): Körper lehnt beim Aufschlag leicht zurück.
    */}
    <g transform="translate(170, 74)">

      {/* Schatten auf Grundlinie */}
      <ellipse cx="2" cy="1" rx="15" ry="3.5" fill="rgba(0,0,0,0.35)" />

      {/* ── Körper (Rack) + Arme in einem gedrehten Kontext ─────────── */}
      <g transform="rotate(-12, 0, -16)">

        {/* Rack-Gehäuse */}
        <rect x="-11" y="-33" width="22" height="33" rx="3"
              fill="#2a2a3a" filter="url(#rackShadow)" />
        {/* Frontblenden */}
        <rect x="-11" y="-33" width="22" height="3.5" rx="1.5" fill="#1a1a28" />
        <rect x="-11" y="-3"  width="22" height="3.5" rx="1.5" fill="#1a1a28" />
        {/* Laufwerkseinschübe (3 Stück) */}
        {[-27, -20, -13].map(y => (
          <g key={y}>
            <rect x="-9" y={y} width="18" height="5" rx="1" fill="#3a3a50" />
            <rect x="-8" y={y+0.5} width="10" height="4" rx="1" fill="#4a4a62" />
            <circle cx="6"  cy={y+2.5} r="1.4" fill="#22ee44" opacity="0.95" />
            <circle cx="9"  cy={y+2.5} r="1.4" fill="#ff9900" opacity="0.8"  />
          </g>
        ))}
        {/* Netzwerk-Ports */}
        <rect x="-9" y="-7" width="18" height="4" rx="1" fill="#222233" />
        {[-7,-4,-1,2,5].map(x => (
          <rect key={x} x={x} y="-6.5" width="2.5" height="3" rx="0.5" fill="#555" />
        ))}
        {/* Status-LED */}
        <circle cx="-8" cy="-31" r="2"
          fill="#22ee44"
          style={{ animation: "tgt-blink 1.3s step-end infinite" }}
        />

        {/* ── Linker Arm (Wurfarm) – senkrecht nach oben gestreckt ── */}
        <line x1="-9"  y1="-26" x2="-16" y2="-42"
              stroke="#2a2a3a" strokeWidth="4.5" strokeLinecap="round" />
        {/* Hand */}
        <circle cx="-17" cy="-44" r="3.5" fill="#3a3a50" />

        {/* ── Rechter Arm (Schlagarm) – angeholt, Schläger oben ── */}
        <line x1="10"  y1="-26" x2="20"  y2="-34"
              stroke="#2a2a3a" strokeWidth="4.5" strokeLinecap="round" />
        <line x1="20"  y1="-34" x2="24"  y2="-24"
              stroke="#2a2a3a" strokeWidth="3.5" strokeLinecap="round" />

        {/* ── Schläger ── */}
        {/* Griff */}
        <rect x="21.5" y="-24" width="3.5" height="12" rx="1.5" fill="#7a4e2c" />
        {/* Griffband */}
        {[-22,-19,-16,-13].map(y => (
          <rect key={y} x="21.5" y={y} width="3.5" height="2" rx="0.5" fill="#5a3a1e" opacity="0.7" />
        ))}
        {/* Kopf */}
        <ellipse cx="24" cy="-32" rx="9" ry="11"
                 fill="none" stroke="#c8a050" strokeWidth="2.2" />
        {/* Saiten horizontal */}
        {[-37,-33,-29,-25,-21].map(y => {
          const hw = Math.sqrt(Math.max(0, 81 - (y+32)*(y+32)*81/121));
          return (
            <line key={y} x1={24-hw} y1={y} x2={24+hw} y2={y}
                  stroke="#c8a050" strokeWidth="0.7" opacity="0.6" />
          );
        })}
        {/* Saiten vertikal */}
        {[18,21,24,27,30].map(x => {
          const hh = Math.sqrt(Math.max(0, 121 - (x-24)*(x-24)*121/81));
          return (
            <line key={x} x1={x} y1={-32-hh} x2={x} y2={-32+hh}
                  stroke="#c8a050" strokeWidth="0.7" opacity="0.6" />
          );
        })}

      </g>{/* end rotate */}

      {/* ── Aufgeworfener Ball (animiert) ──────────────────────────── */}
      <g style={{ animation: "tgt-toss 2.4s ease-in-out infinite" }}>
        {/* Ball: Wurfarm endet bei ca. (-17, -44) nach Rotation ... wir setzen ihn
            etwas weiter oben links: (-26, -58) im translate-Raum */}
        <circle cx="-26" cy="-58" r="7" fill="url(#ballG)" />
        <path d="M-32 -58 Q-29 -64 -26 -64 Q-23 -64 -20 -58"
              fill="none" stroke="white" strokeWidth="1.2" opacity="0.7" />
        <path d="M-32 -58 Q-29 -52 -26 -52 Q-23 -52 -20 -58"
              fill="none" stroke="white" strokeWidth="1.2" opacity="0.7" />
      </g>

    </g>{/* end translate */}

  </svg>
);

/* ── Hauptkomponente ─────────────────────────────────────────────────────── */
const MaintenancePage = () => {
  const { retry } = useBackendHealth();
  const [secs, setSecs] = useState(RETRY_SECS);

  useEffect(() => {
    const timer = setInterval(() => {
      setSecs((s) => (s <= 1 ? RETRY_SECS : s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleRetry = () => {
    setSecs(RETRY_SECS);
    retry();
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(160deg, #1a6aad 0%, #2e8fd4 50%, #4aa8e8 100%)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "Arial, sans-serif",
      color: "#fff",
      padding: "20px",
      boxSizing: "border-box",
      textAlign: "center",
    }}>
      <style>{`
        @keyframes tgt-sun-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes tgt-toss {
          0%   { transform: translateY(0px);   animation-timing-function: cubic-bezier(0.2,0,0.4,1); }
          38%  { transform: translateY(-16px);  animation-timing-function: ease-in-out; }
          55%  { transform: translateY(-18px);  animation-timing-function: cubic-bezier(0.6,0,0.8,1); }
          100% { transform: translateY(0px);   }
        }
        @keyframes tgt-blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.1; }
        }
        .tgt-btn:hover  { transform: scale(1.05); box-shadow: 0 4px 16px rgba(0,0,0,0.35) !important; }
        .tgt-btn:active { transform: scale(0.97); }
      `}</style>

      <img
        src="/TGT_Logo.png"
        alt="TGT Logo"
        style={{
          width: 72, height: 72,
          borderRadius: "50%",
          background: "#fff",
          padding: 7,
          boxSizing: "border-box",
          marginBottom: 10,
          filter: "drop-shadow(0 3px 10px rgba(0,0,0,0.4))",
        }}
      />
      <h1 style={{ fontSize: "1.8rem", fontWeight: "bold", margin: "0 0 3px", letterSpacing: 1 }}>
        TGT Tennis
      </h1>
      <p style={{ margin: "0 0 24px", opacity: 0.8, fontSize: "0.95rem" }}>
        Tennisgemeinschaft Thedinghausen
      </p>

      <div style={{
        background: "rgba(255,255,255,0.22)",
        backdropFilter: "blur(12px)",
        borderRadius: 18,
        padding: "28px 36px",
        maxWidth: 420,
        width: "100%",
        boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        border: "1px solid rgba(255,255,255,0.45)",
      }}>
        <CourtScene />

        <p style={{ fontSize: "1.15rem", fontWeight: "bold", margin: "20px 0 8px" }}>
          Der Server macht ein Break…
        </p>
        <p style={{ fontSize: "0.88rem", opacity: 0.78, margin: "0 0 24px", lineHeight: 1.55 }}>
          Unser Backend startet gerade hoch. Wir sind gleich zurück –
          der Server nutzt die Zeit für einen Aufschlag auf dem Sandplatz.
        </p>

        <button
          className="tgt-btn"
          onClick={handleRetry}
          style={{
            background: "#e8491d",
            color: "#ffffff",
            border: "none",
            borderRadius: 8,
            padding: "10px 28px",
            fontSize: "0.95rem",
            fontWeight: "bold",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            transition: "transform 0.12s, box-shadow 0.12s",
          }}
        >
          Nochmal versuchen
        </button>

        <p style={{ fontSize: "0.78rem", opacity: 0.6, marginTop: 12 }}>
          Automatischer Versuch in {secs} Sek.
        </p>
      </div>
    </div>
  );
};

export default MaintenancePage;
